import { useState } from "react";
import * as Icons from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import type { Block } from "@/types/proposals";

interface IconBlockProps {
  block: Block;
  onUpdate: (updates: Partial<Block>) => void;
  onSelect: () => void;
}

export function IconBlock({ block, onUpdate, onSelect }: IconBlockProps) {
  const [search, setSearch] = useState("");
  const iconName = block.content?.iconName || "Star";
  const baseSize = block.content?.iconSize || Math.min(block.position.width, block.position.height === 'auto' ? 100 : block.position.height) / 2;
  const strokeWidth = block.content?.strokeWidth || 2;
  const color = block.style?.textColor || "#000000";
  const blockSize = block.settings?.blockSize || 'medium';
  const textAlign = block.style?.textAlign || 'left';

  const sizeMultiplier = {
    small: 0.75,
    medium: 1,
    large: 1.5,
  }[blockSize];

  const size = baseSize * sizeMultiplier;

  const containerAlignClass = {
    left: 'justify-start',
    center: 'justify-center',
    right: 'justify-end',
  }[textAlign];

  const IconComponent = (Icons as any)[iconName] || Icons.Star;

  const iconList = Object.keys(Icons)
    .filter((name) => {
      // Filter out non-icon exports
      if (name === "createLucideIcon" || name === "default") return false;
      return name.toLowerCase().includes(search.toLowerCase());
    })
    .slice(0, 200); // Limit to 200 icons for performance

  return (
    <div 
      className={`w-full h-full flex items-center ${containerAlignClass}`} 
      onClick={onSelect}
    >
      <Popover>
        <PopoverTrigger asChild>
          <button className="hover:scale-110 transition-transform p-2">
            <IconComponent size={size} color={color} strokeWidth={strokeWidth} />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-80">
          <div className="space-y-2">
            <Input
              placeholder="Search icons..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <ScrollArea className="h-64">
              <div className="grid grid-cols-6 gap-2 p-2">
                {iconList.map((name) => {
                  const Icon = (Icons as any)[name];
                  return (
                    <Button
                      key={name}
                      variant={name === iconName ? "default" : "ghost"}
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        onUpdate({
                          content: { ...block.content, iconName: name },
                        });
                      }}
                      className="h-10 w-10 p-0"
                      title={name}
                    >
                      <Icon size={20} />
                    </Button>
                  );
                })}
              </div>
            </ScrollArea>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
